import { Injectable, Type } from '@angular/core';
import { Subject } from 'rxjs';
import { InputValue } from 'components/virtual-keyboard/input-value';
import { LoggingService } from './logging.service';
import { PopoverController } from 'providers/popover-controller';
import { CfPopoverInterface } from 'components/popover-host/popover-interface';
import { BackdropMode } from 'components/popover-host/popover-host';

export enum BorrowedElementAction {
  Focus = 'focus',  // Focus next input  
  Click = 'click'   // Click the button
}

export enum KeyCode {
  Num0 = 0,
  Num1 = 1,
  Num2 = 2,
  Num3 = 3,
  Num4 = 4,
  Num5 = 5,
  Num6 = 6,
  Num7 = 7,
  Num8 = 8,
  Num9 = 9,
  NumDot = 10,
  Space = 32,
  Slash = 47,
  Backspace = 100,
  ArrowLeft = 108,
  ArrowRight = 109,
  KeyboardAppeared = 1000,
  Units = 1001,
  Scroller = 1002,
};

import { MeasureUnitConfig } from '@cf-shared/measure-configs';

export interface KeyboardOptions {
  allowFractions?: boolean; // enables Space and Slash keys
  allowUnits?: boolean; // enables Units key
  allowScroller?: boolean; // enables Value Scroller key
  allowDecimal?: boolean; // enables NumDot key
  decimalSeparator?: string; // Decimal separator to display and insert (default: '.')
  initialDisplayText?: string; // Initial text to display in keyboard (for mobile where div doesn't have .value)
  // Callback for real-time text updates as user types. It may return a
  // corrected version of what it was given — the text the parent actually
  // kept after clamping it into range or dropping characters past the field's
  // length — and the keyboard then shows that instead of what was typed, so
  // its preview never disagrees with the input it is editing. Returning
  // nothing leaves the typed text on screen unchanged (#4904).
  onTextChange?: (text: string) => void | string;
  unitConfigs?: MeasureUnitConfig[]; // Available unit configurations
  currentUnitId?: string; // Currently selected unit ID
  alwaysShowUnit?: boolean; // Always show unit even when there's only one unit option
  onUnitChange?: (unitId: string) => void | Promise<void>; // Callback when unit is changed (can be async)
  isCustomUnitConfig?: boolean; // True when using customUnitConfigs
  startInRollerMode?: boolean; // Start keyboard in roller mode instead of keypad
  // This control picks from a fixed list of values, so only the roller makes
  // sense for it: the view toggle is hidden and the keypad cannot be reached.
  // Unlike startInRollerMode, this also overrides the view the user last
  // chose — otherwise somebody whose remembered view is the keypad would open
  // onto a keypad with no toggle to get back. Used by the rest-timer pickers,
  // whose M:SS durations have no unambiguous typed form (#4904).
  rollerOnly?: boolean;
  acceptButtonLabel?: string; // Custom label for the accept/log button (default: 'Accept')
  onClose?: () => void; // Callback when keyboard is closed
  targetElement?: HTMLElement; // The current element that the keyboard is attached to (input on desktop, div on mobile)

  // Roller configuration
  // For single roller: only preselectValuesNumeric1, suggestedValueNumeric1, rollerLabel1 are used
  // For dual roller: all properties are used
  isDualRoller?: boolean; // True if this is a dual roller (e.g., feet and inches)
  // InputValue arrays for roller
  preselectValuesNumeric1?: InputValue[]; // InputValue[] for first/single roller 
  preselectValuesNumeric2?: InputValue[]; // InputValue[] for second roller (dual mode only)
  suggestedValueNumeric1?: number; // Suggested numeric value for first/single roller
  suggestedValueNumeric2?: number; // Suggested numeric value for second roller (dual mode only)
  currentValueNumeric1?: number; // Current actual numeric value for first unit (for dual units)
  currentValueNumeric2?: number; // Current actual numeric value for second unit (for dual units)
  rollerLabel1?: string; // Label for first/single roller
  rollerLabel2?: string; // Label for second roller (dual mode only)
  // Callback uses numbers only
  onValueSelectedNumeric?: (values: { value1: number; value2?: number }) => void; // Numeric callback when value(s) are selected
  // Lightweight per-tick preview while the roller is still moving: updates the
  // input's visible text only. The expensive commit work (editDone and what
  // hangs off it, e.g. the plate-loading solver) must NOT run here — it runs
  // in onValueSelectedNumeric once the wheel settles (#4042)
  onValuePreviewNumeric?: (values: { value1: number; value2?: number }) => void;
  // When the Next button moves focus on from a two-wheel picker, skip the input
  // that supplies the second wheel: the user has just set it with that wheel, so
  // stopping there again would make them press Next twice for one value.
  shouldSkipNextInSequence?: boolean;
};

export type KeyboardOptionsProvider = () => KeyboardOptions;

@Injectable()
export class VirtualKeyboardService {
  public keyboardHeight = 0; // used for the keyboard sizing itself
  private keyboardComponentType: Type<CfPopoverInterface> = null; // Lazy loaded component type
  private contentComponents: any[] = []; // CsContentComponent[]
  private currentKeyboardInstance: any = null; // Reference to current keyboard component instance in popover
  private presentPending = false; // Guard against multiple concurrent present calls
  private _currentTargetElement: HTMLElement | undefined; // Track target immediately, before keyboard animates

  // Emits when keyboard target or state changes - components subscribe to update their display
  readonly targetChanged$ = new Subject<void>();

  // Expose the current target element for active state checking
  // This is the source of truth for which input is currently active
  get currentTargetElement(): HTMLElement | undefined {
    return this._currentTargetElement;
  }

  // Expose keyboard state for input components to render cursor/selection
  get isValueSelected(): boolean {
    return this.currentKeyboardInstance?.isValueSelected ?? false;
  }

  get isKeypadMode(): boolean {
    // KeyboardView.Keyboard = 'keyboard'
    return this.currentKeyboardInstance?.currentView === 'keyboard';
  }

  constructor(
    private logging: LoggingService,
    private popoverCtrl: PopoverController,
  ) {
  }


  // Lazy load the keyboard component type to avoid circular dependency
  private async getKeyboardComponentType(): Promise<Type<CfPopoverInterface>> {
    if (!this.keyboardComponentType) {
      const module = await import('components/virtual-keyboard/virtual-keyboard');
      this.keyboardComponentType = module.VirtualKeyboardComponent;
    }
    return this.keyboardComponentType;
  }

  async startKeyboard(targetElement: HTMLElement, optsProvider: KeyboardOptionsProvider) {
    console.log('[VKService] startKeyboard', {
      hasInstance: !!this.currentKeyboardInstance,
      presentPending: this.presentPending
    });

    // Set target immediately for instant visual feedback (blue outline, cursor)
    this._currentTargetElement = targetElement;
    this.targetChanged$.next();

    // If keyboard is already shown, update it with the new target
    // Note: On mobile with internal focus state, we always update when called
    // since the caller (cs-input-measure) manages focus internally
    if (this.currentKeyboardInstance) {
      // If same element, just refresh options (e.g., after unit change)
      // If different element, update keyboard to point to new element
      console.log('[VKService] updating existing keyboard');
      await this.currentKeyboardInstance.showKeyboard(targetElement, optsProvider);
      this.targetChanged$.next(); // notify components to update their active state
      return;
    }

    // Guard against multiple concurrent present calls - only one can proceed
    if (this.presentPending) {
      console.log('[VKService] blocked - present pending');
      return;
    }
    this.presentPending = true;

    // Get the options to pass to the keyboard
    let opts;
    try {
      opts = optsProvider();
    } catch (error) {
      this.logging.trackAbnormalError(`startKeyboard(): error getting keyboard options - ${error}`);
      this._currentTargetElement = undefined;
      this.targetChanged$.next();
      this.presentPending = false;
      return;
    }

    // Create keyboard as a bottom sheet popover
    try {
      const KeyboardComponent = await this.getKeyboardComponentType();

      const result = await this.popoverCtrl.present(
        KeyboardComponent,
        {
          targetElement,
          optsProvider,
          initialOpts: opts
        },
        {
          targetFromElement: targetElement,
          bottomSheet: true,
          backdropMode: BackdropMode.None, // No backdrop - keyboard manages itself via focus/blur
          animate: true,
          overlayPreviousPopover: true, // Don't dismiss parent popovers (e.g., when input is inside another popover)
          returnInstance: true // Return immediately with instance
        }
      );

      // Store the instance for reuse when switching inputs
      this.currentKeyboardInstance = result.instance;
      this.targetChanged$.next(); // notify components to update their active state

      // Set up cleanup when dismissed
      if (result.promise) {
        result.promise.then(() => {
          this.currentKeyboardInstance = null;
          this._currentTargetElement = undefined;
          this.targetChanged$.next(); // notify components keyboard closed
        });
      }
    } catch (error) {
      this.logging.trackAbnormalError(`startKeyboard(): error showing keyboard - ${error}`);
      this._currentTargetElement = undefined;
      this.targetChanged$.next();
    } finally {
      this.presentPending = false;
    }
  }

  async stopKeyboard() {
    if (this.currentKeyboardInstance) {
      // Check if keyboard is showing an internal popover - if so, don't dismiss
      if (this.currentKeyboardInstance.isShowingInternalPopover) {
        return;
      }
      await this.popoverCtrl.dismiss(this.currentKeyboardInstance);
      this.currentKeyboardInstance = null;
      this._currentTargetElement = undefined;
      this.targetChanged$.next(); // notify components keyboard closed
    }
  }

  // Register a cs-content component to receive padding updates
  registerContentComponent(component: any) {
    if (!this.contentComponents.includes(component)) {
      this.contentComponents.push(component);
    }
  }

  // Unregister a cs-content component when it's destroyed
  unregisterContentComponent(component: any) {
    const index = this.contentComponents.indexOf(component);
    if (index !== -1) {
      this.contentComponents.splice(index, 1);
    }
  }

  // Update padding in all registered cs-content components (synchronous for zero-lag)
  setKeyboardPadding(height: number) {
    for (let component of this.contentComponents) {
      if (component?.setContentPaddingForKeyboard) {
        component.setContentPaddingForKeyboard(height);
      }
    }
  }
}
